import React, { useContext, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Contexts
import { AbilityContext } from '../Can/Can';

// Enums
import { Action } from '../../enums/action';

// Constants
import { PAGE_TITLE } from '../../constants/common';

// Store
import { useAppDispatch, useAppSelector } from '../../store/store';

// Hooks
import useIsMobile from '../../hooks/useIsMobile';

const HOLD_DURATION_MS = 2000;

const LockIcon: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M4 6.5V5a4 4 0 1 1 8 0v1.5h.5A1.5 1.5 0 0 1 14 8v5.5A1.5 1.5 0 0 1 12.5 15h-9A1.5 1.5 0 0 1 2 13.5V8a1.5 1.5 0 0 1 1.5-1.5H4Zm1.5-1.5v1.5h5V5a2.5 2.5 0 0 0-5 0Z"
      fill="#969AA1"
    />
  </svg>
);

const UnlockIcon: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M11.5 1a4 4 0 0 0-4 4v1.5h-4A1.5 1.5 0 0 0 2 8v5.5A1.5 1.5 0 0 0 3.5 15h9a1.5 1.5 0 0 0 1.5-1.5V8a1.5 1.5 0 0 0-1.5-1.5H9V5a2.5 2.5 0 0 1 4.958-.487.75.75 0 1 0 1.472-.288A4.002 4.002 0 0 0 11.5 1Z"
      fill="#F08642"
    />
  </svg>
);

// A "child lock" style toggle, separate from the CASL permission system - it doesn't
// grant access, it just guards users (who already have permission) from firing
// start/stop/edit actions with a stray tap while carrying a phone around site. That risk
// doesn't exist on desktop, so there edit mode is implicitly always on and this toggle
// doesn't render at all - see useEditModeEnabled, which every gated action reads from.
// Press-and-hold rather than a plain click so it can't be triggered by accident either.
const EditModeToggle: React.FC = () => {
  const { t } = useTranslation();
  const ability = useContext(AbilityContext);
  const isMobile = useIsMobile();
  const dispatch = useAppDispatch();
  const isEditModeEnabled = useAppSelector((state) => state.edit_mode.isEnabled);

  const [isHolding, setIsHolding] = useState(false);
  const holdTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const canUseEditMode = useMemo(() => ability.can(Action.Update, PAGE_TITLE.CYCLE), [ability]);

  const cancelHold = () => {
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
      holdTimeoutRef.current = null;
    }
    setIsHolding(false);
  };

  const startHold = () => {
    if (holdTimeoutRef.current) return;
    setIsHolding(true);
    holdTimeoutRef.current = setTimeout(() => {
      dispatch.edit_mode.setEnabled(!isEditModeEnabled);
      cancelHold();
    }, HOLD_DURATION_MS);
  };

  if (!canUseEditMode || !isMobile) return null;

  const label = isEditModeEnabled
    ? t('header.editModeOn', 'Edit Mode')
    : t('header.editModeOff', 'View Only');
  const holdHint = t('header.editModeHoldHint', 'Press and hold for 2 seconds to toggle');

  return (
    <div className="c-edit-mode-toggle flex select-none flex-col items-center gap-1">
      <div
        role="switch"
        aria-checked={isEditModeEnabled}
        aria-label={label}
        title={holdHint}
        tabIndex={0}
        onPointerDown={startHold}
        onPointerUp={cancelHold}
        onPointerLeave={cancelHold}
        onPointerCancel={cancelHold}
        onContextMenu={(event) => event.preventDefault()}
        onKeyDown={(event) => {
          if ((event.key === ' ' || event.key === 'Enter') && !event.repeat) startHold();
        }}
        onKeyUp={(event) => {
          if (event.key === ' ' || event.key === 'Enter') cancelHold();
        }}
        style={{ touchAction: 'none' }}
        className={`relative m-0 block h-7.5 w-14 cursor-pointer overflow-hidden rounded-full transition-colors duration-300 ${
          isEditModeEnabled ? 'bg-primary' : 'bg-stroke'
        }`}
      >
        {isHolding && (
          <span
            className="c-hold-progress-fill absolute inset-y-0 left-0 bg-black/15 dark:bg-white/25"
            style={{ animationDuration: `${HOLD_DURATION_MS}ms` }}
          />
        )}
        <span
          className={`absolute top-1/2 left-[3px] flex h-6 w-6 -translate-y-1/2 translate-x-0 items-center justify-center rounded-full bg-white shadow-switcher duration-150 ease-linear ${
            isEditModeEnabled ? '!right-[3px] !left-auto' : ''
          }`}
        >
          {isEditModeEnabled ? <UnlockIcon /> : <LockIcon />}
        </span>
      </div>
      <span className="max-sm:hidden text-xs font-medium text-body dark:text-bodydark">{label}</span>
    </div>
  );
};

export default EditModeToggle;
